import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { api, ApiError } from '@/api/client'
import type { ProblemCategory, ProblemRow } from '@/api/types'

const SEVERITY_CLASS: Record<string, string> = {
  high: 'sev-high',
  medium: 'sev-medium',
  low: 'sev-low',
}

/** Where a given problem actually gets fixed. */
function fixLink(fixAt: string, key: string): { to: string; label: string } | null {
  switch (fixAt) {
    case 'job':
      return { to: `/office/jobs/${key}`, label: 'Open job' }
    case 'crews':
      return { to: '/office/crews', label: 'Crews' }
    case 'wage-rates':
      return { to: '/office/wage-rates', label: 'Wage rates' }
    case 'submission':
      // A moved crew day: reclassify its lines there and the row clears.
      return { to: `/office/submissions/${key}`, label: 'Open crew day' }
    case 'employee-certs':
      // Cert problems key as "EmployeeId|..." so any of them can open the
      // person's certifications, whatever follows the bar.
      return { to: `/certs/people/${key.split('|')[0]}`, label: 'Open certifications' }
    case 'crew-week':
      // The week and person are named in the row's detail; the grid is where
      // the hours are, and where overtime gets applied.
      return { to: '/office/crew-week', label: 'Crew week' }
    default:
      // 'sage' — nothing the portal can do; the fix is in Sage itself.
      return null
  }
}

/**
 * Data problems — everything that is set up wrong, in one place.
 *
 * Every row is computed fresh on each load, so fixing the underlying data
 * makes it disappear rather than needing to be marked done. The only stored
 * state is a dismissal, for rows that were looked at and judged fine.
 *
 * Categories with a count of zero still appear. That says the check ran and
 * found nothing, which is worth knowing and is different from the check having
 * been quietly dropped.
 */
export function ProblemsPage() {
  const [cats, setCats] = useState<ProblemCategory[]>([])
  const [open, setOpen] = useState<string | null>(null)
  const [showDismissed, setShowDismissed] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      setCats(await api.problems(showDismissed))
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load problems')
    }
  }, [showDismissed])

  useEffect(() => {
    void load()
  }, [load])

  const total = useMemo(() => cats.reduce((s, c) => s + c.count, 0), [cats])

  async function dismiss(cat: ProblemCategory, row: ProblemRow) {
    const reason = window.prompt(
      `Why is "${row.title}" fine?\n\nThis is recorded against your name.`,
      '',
    )
    if (reason === null) return
    if (!reason.trim()) {
      setError('A reason is required — it is the part worth keeping.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      const updated = await api.dismissProblem({
        problem_type: cat.problem_type,
        entity_key: row.entity_key,
        reason: reason.trim(),
      })
      setCats((prev) =>
        prev.map((c) => (c.problem_type === updated.problem_type ? updated : c)),
      )
      setNotice(`${row.title} dismissed.`)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not dismiss')
    } finally {
      setBusy(false)
    }
  }

  async function undismiss(cat: ProblemCategory, row: ProblemRow) {
    setBusy(true)
    setError(null)
    try {
      const updated = await api.undismissProblem(cat.problem_type, row.entity_key)
      setCats((prev) =>
        prev.map((c) => (c.problem_type === updated.problem_type ? updated : c)),
      )
      setNotice(`${row.title} back on the list.`)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not restore')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="stack">
      {error && <div className="alert alert-error">{error}</div>}
      {notice && <div className="alert alert-info">{notice}</div>}

      <div className="card">
        <div className="crew-heading">
          <h2 className="title" style={{ margin: 0 }}>
            Problems
            {total > 0 && <span className="count-badge">{total}</span>}
          </h2>
          <button className="btn btn-secondary" onClick={() => void load()} disabled={busy}>
            Recheck
          </button>
        </div>
        <p className="muted">
          Everything the portal can tell is set up wrong. Each check runs fresh —
          fix the data and the row disappears. {total === 0 && 'Nothing outstanding.'}
        </p>
        <label className="checkline">
          <input
            type="checkbox"
            checked={showDismissed}
            onChange={(e) => setShowDismissed(e.target.checked)}
          />
          <span>Include rows already dismissed</span>
        </label>
      </div>

      {cats.map((c) => {
        const isOpen = open === c.problem_type
        return (
          <div className={`card problem-card ${SEVERITY_CLASS[c.severity] ?? ''}`}
               key={c.problem_type}>
            <button
              className="problem-head"
              onClick={() => setOpen(isOpen ? null : c.problem_type)}
              aria-expanded={isOpen}
            >
              <span className={`count-badge ${c.count === 0 ? 'is-clear' : ''}`}>
                {c.count}
              </span>
              <span className="problem-title">
                <strong>{c.title}</strong>
                <span className="muted" style={{ display: 'block', fontSize: '0.8125rem' }}>
                  {c.consequence}
                </span>
              </span>
              {c.dismissed_count > 0 && (
                <span className="muted" style={{ fontSize: '0.75rem' }}>
                  {c.dismissed_count} dismissed
                </span>
              )}
              <span aria-hidden="true">{isOpen ? '▾' : '▸'}</span>
            </button>

            {isOpen && (
              <div className="table-wrap" style={{ marginTop: '0.75rem' }}>
                <table>
                  <thead>
                    <tr>
                      <th>What</th>
                      <th>Detail</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {c.rows.map((r) => {
                      const link = fixLink(c.fix_at, r.entity_key)
                      return (
                        <tr key={r.entity_key} className={r.dismissed ? 'is-dismissed' : ''}>
                          <td className="mono">{r.title}</td>
                          <td>
                            {r.detail}
                            {r.dismissed && (
                              <div className="muted" style={{ fontSize: '0.75rem' }}>
                                Dismissed by {r.dismissed_by}: {r.dismissed_reason}
                              </div>
                            )}
                          </td>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            {link && (
                              <Link className="btn btn-secondary btn-sm" to={link.to}>
                                {link.label}
                              </Link>
                            )}{' '}
                            {r.dismissed ? (
                              <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => void undismiss(c, r)}
                                disabled={busy}
                              >
                                Restore
                              </button>
                            ) : (
                              <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => void dismiss(c, r)}
                                disabled={busy}
                              >
                                This is fine
                              </button>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                    {c.rows.length === 0 && (
                      <tr>
                        <td colSpan={3} className="muted">
                          Nothing found by this check.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
